import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { HtmlDocumentPreview } from './HtmlDocumentPreview'
import { PdfDocumentPreview } from './PdfDocumentPreview'
import { describePreviewKind } from './previewKind'
import { SlideDocumentPreview } from './SlideDocumentPreview'
import { TabularDocumentPreview } from './TabularDocumentPreview'
import { VideoPreview } from './VideoPreview'
import { WebsitePreview } from './WebsitePreview'

export interface PagePreviewState {
  pageNumber: number
  text: string
  imageBase64?: string | null
  imageUrl?: string | null
}

export function pagePreviewImageSrc(preview: PagePreviewState): string | null {
  if (preview.imageUrl) {
    return preview.imageUrl
  }
  if (!preview.imageBase64) {
    return null
  }
  // PNG base64 starts with iVBOR; new path returns JPEG.
  const isPng = preview.imageBase64.startsWith('iVBOR')
  return `data:image/${isPng ? 'png' : 'jpeg'};base64,${preview.imageBase64}`
}

/**
 * True for PDFs specifically (not any other paged document type) — the only
 * kind that gets a real pdf.js render instead of the server-rasterized
 * JPEG + extracted-text fallback.
 */
export function isPdfDocument(
  mimeType: string | null | undefined,
  originalFilename: string | null | undefined,
): boolean {
  const mime = (mimeType || '').toLowerCase()
  if (mime.includes('pdf')) {
    return true
  }
  const name = (originalFilename || '').toLowerCase()
  return name.endsWith('.pdf')
}

export interface PreviewPaneProps {
  documentToken: string
  mimeType: string | null
  originalFilename?: string | null
  sourceUrl?: string | null
  linkThumbnailUrl?: string | null
  title: string
  /** The page currently on screen, or null while the first span loads. */
  preview: PagePreviewState | null
  /** Page numbers the rail can jump to. Empty for full-bleed renderers. */
  availablePages: Array<number>
  /** Highest page number known so far; null while still unknown. */
  maxKnownPage: number | null
  pagesLoading: boolean
  /** A deep link pointed at a page we could not resolve. */
  pageUnknown: boolean
  /** `?t=` media timestamp, forwarded to the audio/video renderer. */
  startAt?: string | null
  downloading: boolean
  onSelectPage: (pageNumber: number) => void
  onDownload: () => void
  className?: string
}

/**
 * Left column of the document page: the rendered document plus its pager and
 * download action. Self-contained on purpose. PDFs get a real pdf.js render
 * (`PdfDocumentPreview`); every other paged document keeps the server-
 * rasterized image + extracted-text fallback below, which a PDF also falls
 * back to if pdf.js cannot render it.
 */
export function PreviewPane(props: PreviewPaneProps) {
  const {
    documentToken,
    mimeType,
    originalFilename,
    sourceUrl,
    linkThumbnailUrl,
    title,
    preview,
    availablePages,
    maxKnownPage,
    pagesLoading,
    pageUnknown,
    startAt,
    downloading,
    onSelectPage,
    onDownload,
    className,
  } = props
  const { t } = useTranslation('app')

  const [mediaSrc, setMediaSrc] = useState<string | null>(null)
  // Whether the pdf.js render for this document has failed (fetch error,
  // corrupt file, unparseable page). Reset per document so a fresh document
  // always gets a fresh attempt.
  const [pdfRenderFailed, setPdfRenderFailed] = useState(false)

  const kind = describePreviewKind({ mimeType, originalFilename, sourceUrl })
  const {
    isAudioDocument,
    isFullBleedPreview,
    isHtmlUpload,
    isLinkDocument,
    isSlideUpload,
    isUploadedMedia,
    isVideoDocument,
    tabularFormat,
    usesFlexPreviewBody,
  } = kind
  const resolvedSourceUrl = kind.sourceUrl
  const isPdf = isPdfDocument(mimeType, originalFilename)

  useEffect(() => {
    setPdfRenderFailed(false)
  }, [documentToken])

  useEffect(() => {
    if (!isUploadedMedia || !documentToken) {
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    void knowledgeApi
      .downloadDocument(documentToken)
      .then(({ downloadUrl }) => {
        if (cancelled) {
          if (typeof URL.revokeObjectURL === 'function') {
            URL.revokeObjectURL(downloadUrl)
          }
          return
        }
        objectUrl = downloadUrl
        setMediaSrc(downloadUrl)
      })
      .catch(() => {
        if (!cancelled) {
          setMediaSrc(null)
        }
      })
    return () => {
      cancelled = true
      if (objectUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl)
      }
      setMediaSrc(null)
    }
  }, [isUploadedMedia, documentToken])

  const currentPage = preview?.pageNumber ?? 1
  const canGoPrevious = kind.isPagedDocument && currentPage > 1
  const canGoNext =
    kind.isPagedDocument &&
    (maxKnownPage == null || currentPage < maxKnownPage)

  function handlePrevious() {
    onSelectPage(Math.max(1, currentPage - 1))
  }

  function handleNext() {
    onSelectPage(currentPage + 1)
  }

  const pageLabel = isAudioDocument
    ? startAt
      ? t('library.preview.audioAt', { time: startAt })
      : t('library.preview.audio')
    : isVideoDocument
      ? startAt
        ? t('library.preview.videoAt', { time: startAt })
        : t('library.preview.video')
      : isLinkDocument
        ? t('library.preview.website')
        : isHtmlUpload
          ? t('library.preview.webPage')
          : maxKnownPage != null
            ? t(
                isSlideUpload
                  ? 'library.preview.slideOf'
                  : tabularFormat?.unitNoun === 'sheet'
                    ? 'library.preview.sheetOf'
                    : 'library.preview.pageOf',
                { current: currentPage, total: maxKnownPage },
              )
            : t(
                isSlideUpload
                  ? 'library.preview.slideN'
                  : tabularFormat?.unitNoun === 'sheet'
                    ? 'library.preview.sheetN'
                    : 'library.preview.pageN',
                { n: currentPage },
              )

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden bg-background',
        className,
      )}
      data-testid="knowledge-doc-preview-pane"
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="flex min-w-0 items-center gap-1">
          {kind.isPagedDocument ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('library.preview.prevPage')}
              disabled={!canGoPrevious || pagesLoading}
              onClick={handlePrevious}
              data-testid="knowledge-doc-page-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          ) : null}
          <Text
            size="sm"
            color="muted"
            className="truncate"
            data-testid="knowledge-preview-page-label"
          >
            {pagesLoading && kind.isPagedDocument && !preview
              ? t('library.preview.loadingPages')
              : pageLabel}
          </Text>
          {kind.isPagedDocument ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('library.preview.nextPage')}
              disabled={!canGoNext || pagesLoading}
              onClick={handleNext}
              data-testid="knowledge-doc-page-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {isLinkDocument && resolvedSourceUrl && !isVideoDocument ? (
            <Button asChild variant="outline" size="sm" className="h-8">
              <a
                href={resolvedSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="knowledge-preview-open-website"
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {t('library.preview.openWebsite')}
              </a>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={downloading}
            onClick={onDownload}
            data-testid="knowledge-doc-download"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {downloading ? t('library.preview.preparing') : t('library.preview.download')}
          </Button>
        </div>
      </div>

      <Separator />

      <div className="flex min-h-0 flex-1">
        {isFullBleedPreview || isPdf ? null : (
          <div
            className="w-16 shrink-0 space-y-2 overflow-y-auto border-r border-card-border p-2 md:w-20"
            data-testid="knowledge-preview-rail"
          >
            {availablePages.map((n) => (
              <button
                key={n}
                type="button"
                className={`flex aspect-[3/4] w-full items-center justify-center rounded-md border text-xs ${
                  preview?.pageNumber === n
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-card-border text-muted-foreground hover:bg-muted'
                }`}
                data-testid={`knowledge-preview-thumb-${n}`}
                onClick={() => onSelectPage(n)}
              >
                {n}
              </button>
            ))}
          </div>
        )}
        <div
          className={cn(
            'min-h-0 flex-1 bg-background',
            usesFlexPreviewBody
              ? 'flex overflow-hidden p-3 md:p-4'
              : 'overflow-y-auto p-4 md:p-6',
          )}
          data-testid="knowledge-preview-body"
        >
          {pageUnknown ? (
            <Text
              size="sm"
              className="mb-3 text-chart-warning"
              data-testid="knowledge-preview-page-unknown"
            >
              {t('library.preview.pageUnknown')}
            </Text>
          ) : null}
          {isUploadedMedia && mediaSrc ? (
            <VideoPreview
              url={mediaSrc}
              title={title}
              transcript={preview?.text}
              startAt={startAt}
              mediaKind={isAudioDocument ? 'audio' : 'video'}
              hideOpenSource
            />
          ) : isUploadedMedia ? (
            <Text size="sm" color="muted">
              {t('library.preview.loadingRecording')}
            </Text>
          ) : isVideoDocument && resolvedSourceUrl ? (
            <VideoPreview
              url={resolvedSourceUrl}
              title={title}
              transcript={preview?.text}
              startAt={startAt}
            />
          ) : isLinkDocument && resolvedSourceUrl ? (
            <WebsitePreview
              url={resolvedSourceUrl}
              title={title}
              thumbnailUrl={linkThumbnailUrl}
              fallbackText={preview?.text}
            />
          ) : isHtmlUpload && documentToken ? (
            <HtmlDocumentPreview
              documentToken={documentToken}
              title={title}
              extractedText={preview?.text}
            />
          ) : isSlideUpload && documentToken && preview ? (
            <SlideDocumentPreview
              documentToken={documentToken}
              title={title}
              pageNumber={preview.pageNumber}
              extractedText={preview.text}
            />
          ) : tabularFormat && preview ? (
            <TabularDocumentPreview
              title={title}
              pageNumber={preview.pageNumber}
              format={tabularFormat}
              extractedText={preview.text}
            />
          ) : isPdf && documentToken && !pdfRenderFailed ? (
            <PdfDocumentPreview
              documentToken={documentToken}
              pageNumber={currentPage}
              title={title}
              onUnavailable={() => setPdfRenderFailed(true)}
              onVisiblePageChange={onSelectPage}
            />
          ) : preview ? (
            <div
              className="mx-auto max-w-3xl rounded-xl border border-card-border bg-document-page p-6 text-document-page-foreground shadow-sm"
              data-testid="knowledge-preview-legacy-page"
            >
              {(() => {
                const src = pagePreviewImageSrc(preview)
                return src ? (
                  <img
                    src={src}
                    alt={t('library.preview.pageAlt', { n: preview.pageNumber })}
                    className="mb-4 max-w-full"
                  />
                ) : null
              })()}
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-document-page-foreground">
                {preview.text || t('library.preview.noText')}
              </pre>
              <div className="mt-8 flex justify-between text-xs text-document-page-muted">
                <span>{title}</span>
                <span>{t('library.preview.pageShort', { n: preview.pageNumber })}</span>
              </div>
            </div>
          ) : (
            <Text size="sm" color="muted" data-testid="knowledge-preview-empty">
              {pagesLoading ? t('library.preview.loading') : t('library.preview.empty')}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
